"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.escapeRegExCharacters = escapeRegExCharacters;
exports.boolValue = boolValue;
exports.unescapeHTML = unescapeHTML;
exports.escapeInterpolationDataStrings = escapeInterpolationDataStrings;
exports.attachResourceToDom = attachResourceToDom;
exports.evaluate = evaluate;
exports.interpolate = interpolate;
exports.hasValue = hasValue;
exports.doesArrayDataHaveValue = doesArrayDataHaveValue;
exports.isEmptyObject = isEmptyObject;
exports.valueIsPresent = valueIsPresent;
exports.isAddressComponent = isAddressComponent;
exports.isDayComponent = isDayComponent;
exports.isContainerComponent = isContainerComponent;
exports.isAddressComponentDataObject = isAddressComponentDataObject;
exports.isComponentThatCannotHaveFalseValue = isComponentThatCannotHaveFalseValue;
exports.componentHasValue = componentHasValue;
const lodash_1 = require("lodash");
const Evaluator_1 = require("./Evaluator");
const formUtil_1 = require("./formUtil");
/**
 * Escapes RegEx characters in provided String value.
 *
 * @param {String} value
 *   String for escaping RegEx characters.
 * @returns {string}
 *   String with escaped RegEx characters.
 */
function escapeRegExCharacters(value) {
    return value.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
}
/**
 * Determines the boolean value of a setting.
 *
 * @param value
 * @return {boolean}
 */
function boolValue(value) {
    if ((0, lodash_1.isBoolean)(value)) {
        return value;
    }
    else if ((0, lodash_1.isString)(value)) {
        return value.toLowerCase() === 'true';
    }
    else {
        return !!value;
    }
}
/**
 * Unescape HTML characters like &lt, &gt, &amp and etc.
 * @param str
 * @returns {string}
 */
function unescapeHTML(str) {
    if (typeof window === 'undefined' || !('DOMParser' in window)) {
        return str;
    }
    const doc = new window.DOMParser().parseFromString(str, 'text/html');
    return doc.documentElement.textContent;
}
/**
 * Recursively HTML-escape strings in submission-like objects so logic templates
 * (e.g. dynamic labels) cannot treat user-entered values as HTML.
 */
function escapeInterpolationDataStrings(obj) {
    if (obj === null || obj === undefined) {
        return obj;
    }
    if (typeof obj === 'string') {
        return (0, lodash_1.escape)(obj);
    }
    if (Array.isArray(obj)) {
        return obj.map(escapeInterpolationDataStrings);
    }
    if ((0, lodash_1.isPlainObject)(obj)) {
        return (0, lodash_1.mapValues)(obj, escapeInterpolationDataStrings);
    }
    return obj;
}
function attachResourceToDom(options) {
    const { name, formio, onload, rootElement, onerror } = options;
    let { src } = options;
    src = Array.isArray(src) ? src : [src];
    src.forEach((lib) => {
        let attrs = {};
        let elementType = '';
        if (typeof lib === 'string') {
            lib = {
                type: 'script',
                src: lib,
            };
        }
        switch (lib.type) {
            case 'script':
                elementType = 'script';
                attrs = {
                    src: lib.src,
                    type: 'text/javascript',
                    defer: true,
                    async: true,
                    referrerpolicy: 'origin',
                };
                break;
            case 'styles':
                elementType = 'link';
                attrs = {
                    href: lib.src,
                    rel: 'stylesheet',
                };
                break;
        }
        // Add the script to the top of the page.
        const element = document.createElement(elementType);
        if (element.setAttribute) {
            for (const attr in attrs) {
                element.setAttribute(attr, attrs[attr]);
            }
        }
        if (onload) {
            element.addEventListener('load', () => {
                formio.libraries[name].loaded = true;
                onload(formio.libraries[name].ready);
            });
        }
        element.addEventListener('error', (e) => {
            console.warn(`Unable to load script ${name}`);
            if (typeof onerror === 'function') {
                onerror(e);
            }
        });
        if (rootElement) {
            rootElement.insertAdjacentElement('afterend', element);
            return;
        }
        const { head } = document;
        if (head) {
            head.appendChild(element);
        }
    });
}
/**
 * A convenience function that wraps Evaluator.evaluate and normalizes context values
 * @param evaluation - The code string to evaluate
 * @param context - The processor context
 * @param ret - The return value
 * @param interpolate - Whether or not to interpolate the code string before evaluating
 * @param evalContextFn - A callback to mutate the context value after it has been normalized
 * @param options - Options to pass to the Evaluator
 * @returns {*} - Returns the result of the evaluation
 */
function evaluate(evaluation, context, ret = 'result', interpolate = false, evalContextFn, options = {}) {
    const { instance, form } = context;
    const normalizedContext = (0, formUtil_1.normalizeContext)(context);
    if (evalContextFn) {
        evalContextFn(normalizedContext);
    }
    if (form === null || form === void 0 ? void 0 : form.module) {
        options = Object.assign(Object.assign({}, options), { formModule: form.module });
    }
    if (instance && instance.evaluate) {
        return instance.evaluate(evaluation, normalizedContext, ret, interpolate, options);
    }
    return Evaluator_1.Evaluator.evaluate(evaluation, normalizedContext, ret, interpolate, context, options);
}
function interpolate(evaluation, context, evalContextFn) {
    const { instance } = context;
    const normalizedContext = (0, formUtil_1.normalizeContext)(context);
    if (evalContextFn) {
        evalContextFn(normalizedContext);
    }
    if (instance && instance.interpolate) {
        return instance.interpolate(evaluation, normalizedContext, {
            noeval: true,
        });
    }
    return Evaluator_1.Evaluator.interpolate(evaluation, normalizedContext, {
        noeval: true,
    });
}
function hasValue(value) {
    if ((0, lodash_1.isObject)(value)) {
        return !(0, lodash_1.isEmpty)(value);
    }
    return (typeof value === 'number' && !Number.isNaN(value)) || !!value;
}
function doesArrayDataHaveValue(dataValue = []) {
    if (!Array.isArray(dataValue)) {
        return !!dataValue;
    }
    if (!dataValue.length) {
        return false;
    }
    const isArrayDataComponent = dataValue.every(lodash_1.isPlainObject);
    if (isArrayDataComponent) {
        return dataValue.some((value) => Object.values(value).some(hasValue));
    }
    return dataValue.some(hasValue);
}
function isEmptyObject(obj) {
    return !!obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}
function valueIsPresent(value, considerFalseTruthy, shouldSkipObjectValue) {
    // Evaluate for 3 out of 6 falsy values ("", null, undefined), don't check for 0
    // and only check for false under certain conditions
    if (value === null ||
        value === undefined ||
        (typeof value === 'string' && !value.trim()) ||
        (!considerFalseTruthy && value === false)) {
        return false;
    }
    // Evaluate for empty object
    else if (isEmptyObject(value)) {
        return false;
    }
    // Evaluate for empty array
    else if (Array.isArray(value) && value.length === 0) {
        return false;
    }
    // Recursively evaluate
    else if (typeof value === 'object' && !shouldSkipObjectValue) {
        return Object.values(value).some((val) => valueIsPresent(val, considerFalseTruthy, shouldSkipObjectValue));
    }
    // If value is an array, check it's children have value
    else if (Array.isArray(value) && value.length) {
        return doesArrayDataHaveValue(value);
    }
    return true;
}
function isAddressComponent(component) {
    return component.type === 'address';
}
function isDayComponent(component) {
    return component.type === 'day';
}
function isContainerComponent(component) {
    return component.type === 'container';
}
function isAddressComponentDataObject(value) {
    return (value !== null &&
        typeof value === 'object' &&
        value.mode &&
        value.address &&
        typeof value.address === 'object');
}
// Checkboxes and selectboxes consider false to be falsy, whereas other components with
// settable values (e.g. radio, select, datamap, container, etc.) consider it truthy
function isComponentThatCannotHaveFalseValue(component) {
    return component.type === 'checkbox' || component.type === 'selectboxes';
}
// Components with multiple masks enabled store their data as { value, maskName } objects
function isMultipleMasksComponent(component) {
    var _a;
    const isComponentWithMaskValue = component.type === 'textfield' || component.type === 'phoneNumber';
    return (isComponentWithMaskValue && !!component.allowMultipleMasks && !!((_a = component.inputMasks) === null || _a === void 0 ? void 0 : _a.length));
}
function isMaskComponentDataObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value) && 'value' in value;
}
function componentHasValue(component, value) {
    if (isMultipleMasksComponent(component) && isMaskComponentDataObject(value)) {
        // The maskName alone does not constitute a value; only the entered value does
        return valueIsPresent(value.value, true);
    }
    if (isAddressComponent(component) && isAddressComponentDataObject(value)) {
        return isEmptyObject(value.address)
            ? false
            : Object.entries(value.address)
                .filter(([key]) => !['address2'].includes(key))
                .every(([, val]) => !!val)
                ? true
                : false;
    }
    else if (isDayComponent(component) && value === '00/00/0000') {
        return false;
    }
    else if (isComponentThatCannotHaveFalseValue(component)) {
        return !valueIsPresent(value, false, (0, formUtil_1.isComponentNestedDataType)(component)) ? false : true;
    }
    return !valueIsPresent(value, true, (0, formUtil_1.isComponentNestedDataType)(component) && !isContainerComponent(component))
        ? false
        : true;
}
